import React, { useState, useRef, useEffect } from 'react';
import {
  Hash,
  Lock,
  Volume2,
  Plus,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Bell,
  Folder,
  Video,
  Trash2,
  UserPlus,
  Settings,
} from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../hooks/useLanguage';
import { ConfirmDeleteModal } from '../modals/ConfirmDeleteModal';
import { api } from '../../lib/api';
import { WorkspaceLogo } from '../common/WorkspaceLogo';
import { UserAvatar } from '../common/UserAvatar';

interface SidebarProps {
  onOpenCreateChannel: (groupId?: string) => void;
  onOpenCreateGroup: () => void;
  onOpenInvite: () => void;
  onOpenNewDm: () => void;
  onOpenSettings?: () => void;
  onOpenProfile?: () => void;
  activeView?: 'chat' | 'dms' | 'activity' | 'files' | 'meetings';
  onSelectView?: (
    view: 'chat' | 'dms' | 'activity' | 'files' | 'meetings'
  ) => void;
  notificationUnreadCount?: number;
}

export const Sidebar: React.FC<SidebarProps> = ({
  onOpenCreateChannel,
  onOpenCreateGroup,
  onOpenInvite,
  onOpenNewDm,
  onOpenSettings,
  onOpenProfile,
  activeView = 'chat',
  onSelectView,
  notificationUnreadCount: notificationUnreadCountProp = 0,
}) => {
  const { currentWorkspace, members, deleteWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { t } = useLanguage();
  const {
    channels,
    groups,
    directConversations,
    activeChat,
    selectChannel,
    selectDm,
    deleteChannel,
    deleteGroup,
  } = useChat();

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'channel' | 'group' | 'workspace';
    id: string;
    name: string;
  } | null>(null);

  const [notificationUnreadCount, setNotificationUnreadCount] = useState(notificationUnreadCountProp);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setNotificationUnreadCount(notificationUnreadCountProp);
  }, [notificationUnreadCountProp]);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    api.notifications
      .unreadCount(currentWorkspace.id)
      .then((count) =>
        setNotificationUnreadCount(
          typeof count === 'number' ? count : (count as any)?.count || 0
        )
      )
      .catch(() => { });

    // Multi-component broadcast sync
    const channel = new BroadcastChannel('sahaj_notifications');
    channel.onmessage = (event: MessageEvent) => {
      const { type, id } = event.data as { type: string; id?: string };
      if (type === 'ALL_READ') {
        setNotificationUnreadCount(0);
      } else if (type === 'ONE_READ' && id) {
        setNotificationUnreadCount((c) => Math.max(0, c - 1));
      }
    };

    return () => channel.close();
  }, [
    currentWorkspace,
    activeView,
  ]);

  const dmUnreadTotal =
    directConversations.reduce(
      (sum, conversation) =>
        sum +
        (conversation.unreadCount || 0),
      0
    );

  /**
   * Toggle a group's expanded/collapsed state.
   */
  const toggleGroup = (
    groupId: string
  ) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: prev[groupId] !== undefined ? !prev[groupId] : false,
    }));
  };

  const currentUserMember = members.find((m) => m.user.id === user?.id);
  const isWorkspaceAdminOrOwner =
    currentUserMember?.role === 'ADMIN' || currentUserMember?.role === 'OWNER';

  const channelsByGroup = groups.map((group) => ({
    group,
    channels: channels.filter((channel) => channel.groupId === group.id),
  }));

  const groupIds = new Set(groups.map((group) => group.id));
  const ungroupedChannels = channels.filter((channel) => !groupIds.has(channel.groupId));

  return (
    <nav className="w-64 bg-sidebar border-r border-sidebar-border flex flex-col select-none flex-shrink-0 relative">
      {/* ============================================================
          Workspace Header & Interactive Dropdown Menu
          ============================================================ */}
      <div className="h-14 px-3.5 border-b border-sidebar-border flex items-center justify-between font-bold text-sidebar-foreground relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          title={`${currentWorkspace?.name || 'Workspace'} (Click for Workspace Menu & Settings)`}
          aria-label={`${currentWorkspace?.name || 'Workspace'} Menu`}
          aria-expanded={isMenuOpen}
          className="flex items-center gap-2 min-w-0 max-w-[185px] p-1.5 -ml-1 rounded-xl hover:bg-muted/70 text-left transition group cursor-pointer"
        >
          <WorkspaceLogo
            workspaceId={currentWorkspace?.id}
            name={currentWorkspace?.name}
            avatarUrl={currentWorkspace?.avatarUrl}
            fallbackToDefaultLogo={!currentWorkspace?.avatarUrl}
            size="sm"
            className="w-7 h-7 rounded-lg shrink-0 group-hover:scale-105 transition-transform shadow-xs"
          />
          <span className="truncate text-sm font-bold text-foreground group-hover:text-primary transition-colors">
            {currentWorkspace?.name || 'Sahaj Connect'}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 shrink-0 ${
              isMenuOpen ? 'rotate-180 text-primary' : 'group-hover:text-foreground'
            }`}
          />
        </button>

        {/* Header Invite Button */}
        <button
          type="button"
          onClick={onOpenInvite}
          title={t('modals.inviteMembers')}
          aria-label={t('modals.inviteMembers')}
          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition shrink-0"
        >
          <UserPlus className="h-4 w-4" />
        </button>

        {/* Floating Workspace Menu Dropdown */}
        {isMenuOpen && (
          <div className="absolute top-13 left-2 right-2 bg-card border border-border rounded-2xl shadow-2xl z-50 p-1.5 flex flex-col gap-0.5 animate-in fade-in zoom-in-95 duration-150">
            {/* Header info */}
            <div className="px-3 py-2.5 border-b border-border/60 flex items-center gap-3">
              <WorkspaceLogo
                workspaceId={currentWorkspace?.id}
                name={currentWorkspace?.name}
                avatarUrl={currentWorkspace?.avatarUrl}
                size="md"
                className="shrink-0 rounded-xl"
              />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-xs text-foreground truncate">{currentWorkspace?.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] text-muted-foreground uppercase font-semibold">
                    {currentUserMember?.role?.toLowerCase() || 'member'}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                  <span className="text-[10px] text-muted-foreground">{members.length} members</span>
                </div>
              </div>
            </div>

            {/* Menu Actions */}
            <div className="pt-1 space-y-0.5">
              {onOpenSettings && (
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onOpenSettings();
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-primary/10 hover:text-primary transition group text-left"
                >
                  <Settings className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="leading-tight">Workspace Settings & Logo</span>
                    <span className="text-[10px] text-muted-foreground font-normal">Change logo, name & preferences</span>
                  </div>
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  onOpenInvite();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-muted transition text-left"
              >
                <UserPlus className="h-4 w-4 text-muted-foreground shrink-0" />
                <span>Invite Teammates</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  onOpenCreateChannel();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-muted transition text-left"
              >
                <Hash className="h-4 w-4 text-muted-foreground shrink-0" />
                <span>Create Channel</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsMenuOpen(false);
                  onOpenCreateGroup();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-muted transition text-left"
              >
                <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                <span>Create Channel Group</span>
              </button>

              {onOpenProfile && (
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    onOpenProfile();
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-foreground hover:bg-muted transition text-left border-t border-border/50 mt-1 pt-2"
                >
                  <UserAvatar
                    userId={user?.id}
                    displayName={user?.displayName}
                    avatarUrl={user?.avatarUrl}
                    size="xs"
                    className="shrink-0 shadow-2xs"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="leading-tight">My Profile & Avatar</span>
                    <span className="text-[10px] text-muted-foreground font-normal truncate">{user?.displayName}</span>
                  </div>
                </button>
              )}

              {(isWorkspaceAdminOrOwner || user?.globalRole === 'SUPER_ADMIN') && currentWorkspace && (
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    setDeleteTarget({
                      type: 'workspace',
                      id: currentWorkspace.id,
                      name: currentWorkspace.name,
                    });
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-destructive hover:bg-destructive/10 transition group text-left border-t border-border/50 mt-1 pt-2"
                >
                  <Trash2 className="h-4 w-4 text-destructive shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="leading-tight">Delete Workspace</span>
                    <span className="text-[10px] text-destructive/70 font-normal">Permanently remove this workspace</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ============================================================
          Navigation Content
          ============================================================ */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4 text-sm">
        {/* Top-level Feature Navigation */}
        <div className="space-y-0.5">
          {/* Direct Messages Hub */}
          <button
            type="button"
            onClick={() => onSelectView?.('dms')}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition ${
              activeView === 'dms'
                ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <MessageSquare className="h-4 w-4 shrink-0" />
              <span className="truncate">{t('sidebar.directMessages')}</span>
            </div>

            {dmUnreadTotal > 0 && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeView === 'dms'
                  ? 'bg-white text-primary'
                  : 'bg-primary text-primary-foreground'
                  }`}
              >
                {dmUnreadTotal}
              </span>
            )}
          </button>

          {/* Activity Feed */}
          <button
            type="button"
            onClick={() => onSelectView?.('activity')}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition ${
              activeView === 'activity'
                ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <Bell className="h-4 w-4 shrink-0" />
              <span className="truncate">{t('sidebar.activity')}</span>
            </div>

            {notificationUnreadCount > 0 && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeView === 'activity'
                    ? 'bg-white text-primary'
                    : 'bg-primary text-primary-foreground'
                }`}
              >
                {notificationUnreadCount}
              </span>
            )}
          </button>

          {/* Files Repository */}
          <button
            type="button"
            onClick={() => onSelectView?.('files')}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition ${
              activeView === 'files'
                ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <Folder className="h-4 w-4 shrink-0" />
              <span className="truncate">{t('sidebar.files')}</span>
            </div>
          </button>

          {/* Meetings Navigation */}
          <button
            type="button"
            onClick={() =>
              onSelectView?.(
                'meetings'
              )
            }
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition ${activeView === 'meetings'
              ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
          >
            <div className="flex items-center gap-2.5">
              <Video className="h-4 w-4" />

              <span>
                Meetings
              </span>
            </div>
          </button>
        </div>

        <hr className="border-primary/10 my-2" />

        {/* ==========================================================
            Groups & Channels
            ========================================================== */}
        <div>
          <div className="flex items-center justify-between px-2 mb-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>{t('sidebar.channels')}</span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onOpenCreateGroup}
                title={t('modals.createChannel')}
                aria-label={t('modals.createChannel')}
                className="p-1 rounded hover:bg-muted hover:text-foreground transition"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Group List */}
          {channelsByGroup.map(({ group, channels: groupChannels }) => {
            const isExpanded = expandedGroups[group.id] !== false;
            const canDeleteGroup = group.createdBy === user?.id || isWorkspaceAdminOrOwner;

            return (
              <div key={group.id} className="mb-2">
                {/* Group Header */}
                <div className="flex items-center justify-between group px-1 py-1 rounded hover:bg-muted/50 text-xs font-semibold text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="flex items-center gap-1.5 flex-1 truncate text-left"
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3 shrink-0" />
                    ) : (
                      <ChevronRight className="h-3 w-3 shrink-0" />
                    )}

                    <span className="truncate">{group.name}</span>
                  </button>

                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => onOpenCreateChannel(group.id)}
                      title={`Create Channel in ${group.name}`}
                      aria-label={`Create Channel in ${group.name}`}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted hover:text-foreground transition"
                    >
                      <Plus className="h-3 w-3" />
                    </button>

                    {canDeleteGroup && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ type: 'group', id: group.id, name: group.name });
                        }}
                        title={`Delete Group "${group.name}"`}
                        aria-label={`Delete Group "${group.name}"`}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Group Channels */}
                {isExpanded && (
                  <div className="space-y-0.5 mt-0.5">
                    {groupChannels.map((channel) => {
                      const isActive =
                        activeView === 'chat' &&
                        activeChat?.type === 'channel' &&
                        activeChat.channel.id === channel.id;

                      const isVoice = channel.type === 'ANNOUNCEMENT';
                      const canDeleteChannel = channel.createdBy === user?.id || isWorkspaceAdminOrOwner;

                      return (
                        <div key={channel.id} className="group/channel relative flex items-center">
                          <button
                            type="button"
                            onClick={() => {
                              selectChannel(channel);
                              onSelectView?.('chat');
                            }}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition ${
                              isActive
                                ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            }`}
                          >
                            {channel.isPrivate ? (
                              <Lock className="h-3.5 w-3.5 shrink-0 opacity-70" />
                            ) : isVoice ? (
                              <Volume2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
                            ) : (
                              <Hash className="h-3.5 w-3.5 shrink-0 opacity-70" />
                            )}

                            <span className="truncate flex-1 pr-6">
                              {channel.name}
                            </span>
                          </button>

                          {canDeleteChannel && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget({ type: 'channel', id: channel.id, name: channel.name });
                              }}
                              title={`Delete Channel "#${channel.name}"`}
                              aria-label={`Delete Channel "#${channel.name}"`}
                              className={`absolute right-1.5 opacity-0 group-hover/channel:opacity-100 p-1 rounded hover:bg-destructive/10 transition ${
                                isActive ? 'text-primary-foreground/80 hover:text-destructive' : 'text-muted-foreground hover:text-destructive'
                              }`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Ungrouped Channels */}
          {ungroupedChannels.length > 0 && (
            <div className="space-y-0.5">
              {ungroupedChannels.map((channel) => {
                const isActive =
                  activeView === 'chat' &&
                  activeChat?.type === 'channel' &&
                  activeChat.channel.id === channel.id;

                const canDeleteChannel = channel.createdBy === user?.id || isWorkspaceAdminOrOwner;

                return (
                  <div key={channel.id} className="group/channel relative flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        selectChannel(channel);
                        onSelectView?.('chat');
                      }}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition ${
                        isActive
                          ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <Hash className="h-3.5 w-3.5 shrink-0 opacity-70" />
                      <span className="truncate flex-1 pr-6">
                        {channel.name}
                      </span>
                    </button>

                    {canDeleteChannel && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ type: 'channel', id: channel.id, name: channel.name });
                        }}
                        title={`Delete Channel "#${channel.name}"`}
                        aria-label={`Delete Channel "#${channel.name}"`}
                        className={`absolute right-1.5 opacity-0 group-hover/channel:opacity-100 p-1 rounded hover:bg-destructive/10 transition ${
                          isActive ? 'text-primary-foreground/80 hover:text-destructive' : 'text-muted-foreground hover:text-destructive'
                        }`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Direct Messages Section */}
        <div>
          <div className="flex items-center justify-between px-2 mb-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>{t('sidebar.directMessages')}</span>

            <button
              type="button"
              onClick={onOpenNewDm}
              title={t('navigation.newDm')}
              aria-label={t('navigation.newDm')}
              className="p-1 rounded hover:bg-muted hover:text-foreground transition"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="space-y-0.5">
            {directConversations.map((dm) => {
              const isActive =
                activeView === 'dms' ||
                (activeView === 'chat' &&
                  activeChat?.type === 'dm' &&
                  activeChat.conversation.id === dm.id);

              const otherMembers = dm.members.filter(
                (m) => m.id !== user?.id
              );

              const name =
                dm.title ||
                (otherMembers.length === 1
                  ? otherMembers[0]?.displayName
                  : otherMembers.map((m) => m.displayName).join(', ')) ||
                t('common.directMessage');

              const firstOther = otherMembers[0];

              return (
                <button
                  type="button"
                  key={dm.id}
                  onClick={() => {
                    selectDm(dm);
                    onSelectView?.('chat');
                  }}
                  className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-left transition ${
                    isActive
                      ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <UserAvatar
                      userId={firstOther?.id}
                      displayName={name}
                      avatarUrl={firstOther?.avatarUrl}
                      size="xs"
                      showStatus
                      className="shrink-0"
                    />

                    <span className="truncate flex-1 text-xs">
                      {name}
                    </span>
                  </div>

                  {dm.unreadCount > 0 && (
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        isActive
                          ? 'bg-white text-primary'
                          : 'bg-primary text-primary-foreground'
                      }`}
                    >
                      {dm.unreadCount}
                    </span>
                  )}
                </button>
              );
            })}

            {directConversations.length === 0 && (
              <p className="px-3 py-1 text-xs text-muted-foreground/60 italic">
                {t('sidebar.noDms')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Invite Member Footer CTA */}
      <div className="p-3 border-t border-sidebar-border bg-transparent">
        <button
          type="button"
          onClick={onOpenInvite}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-dashed border-border hover:border-primary text-xs font-medium text-muted-foreground hover:text-primary transition"
        >
          <UserPlus className="h-3.5 w-3.5" />
          <span>{t('modals.inviteMembers')}</span>
        </button>
      </div>

      {/* Confirm Delete Group/Channel Modal */}
      {deleteTarget && (
        <ConfirmDeleteModal
          isOpen={true}
          onClose={() => setDeleteTarget(null)}
          type={deleteTarget.type}
          itemName={deleteTarget.name}
          onConfirm={async () => {
            if (deleteTarget.type === 'group') {
              await deleteGroup(deleteTarget.id);
            } else if (deleteTarget.type === 'channel') {
              await deleteChannel(deleteTarget.id);
            } else if (deleteTarget.type === 'workspace') {
              await deleteWorkspace(deleteTarget.id);
            }
          }}
        />
      )}
    </nav>
  );
};